/**
 * The page's progress line — audit 2026-10-10, D; drawn as a moving bar since the owner request of
 * 2026-10-24 (`docs/adr/9075`).
 *
 * A run picker only writes `?runId=` and the page below starts its reads; sixteen pages drew
 * nothing while those reads ran, so a press of «Apri l'esecuzione» looked like a press of nothing.
 * One line across the top of the shell, shown whenever TanStack Query has a read in flight, covers
 * every page and every picker without any of them asking for it.
 *
 * - **It waits 150 ms before it shows.** A cached page settles in a frame or two; a line that
 *   flashed on every one would be noise, and the planner would learn to ignore it.
 * - **It is a progress bar of the indeterminate kind** (`role="progressbar"` with no value): the
 *   platform does not say how far a read is, and inventing a percentage would be a number nobody
 *   measured. `aria-live="polite"` lets a screen reader hear it appear once.
 * - **It moves**: a segment runs along a faint track, because the full-width pulse it replaced was
 *   too quiet to notice (owner, 2026-10-24: the page «shows a void»). Under
 *   `prefers-reduced-motion: reduce` it does not move at all: the track is filled, still.
 * - **It takes its colour from the tokens** and sits above the chrome without taking a click.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { prefersReducedMotion } from '../../primitives/motion';
import { useFetchingCount } from '../../primitives/read-activity';

/** How long a read has to run before the line appears. */
export const PROGRESS_SHOW_DELAY_MS = 150;

/** `true` once `on` has been true for `delayMs`; `false` again the moment it stops. */
function useAfterDelay(on: boolean, delayMs: number): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!on) {
      setShown(false);
      return undefined;
    }
    const timer = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(timer);
  }, [on, delayMs]);
  return shown;
}

export function FetchProgressBar(): React.JSX.Element | null {
  const { t } = useTranslation();
  const fetching = (useFetchingCount() ?? 0) > 0;
  const shown = useAfterDelay(fetching, PROGRESS_SHOW_DELAY_MS);
  if (!shown) return null;
  const still = prefersReducedMotion();
  return (
    <div
      role="progressbar"
      aria-label={t('state.loadingProgress')}
      aria-live="polite"
      aria-busy="true"
      data-fetch-progress={still ? 'still' : 'moving'}
      className="bg-surface-sunken pointer-events-none fixed start-0 end-0 top-0 z-toast h-[3px] overflow-hidden print:hidden"
    >
      {still
        ? <div className="bg-ink-primary h-full w-full" />
        : <div className="bg-ink-primary h-full w-1/3 animate-indeterminate motion-reduce:w-full motion-reduce:animate-none" />}
    </div>
  );
}
